import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import DegradedStorageNotice from "@/components/DegradedStorageNotice";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClientInstance } from "@/lib/query-client";
import NavigationTracker from "@/lib/NavigationTracker";
import { pagesConfig } from "./pages.config";
import {
  BrowserRouter,
  MemoryRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import PageNotFound from "./lib/PageNotFound";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import UserNotRegisteredError from "@/components/UserNotRegisteredError";
import MeansCalculator from "@/pages/MeansCalculator";
import WhatAidDoINeed from "@/pages/WhatAidDoINeed";
import FeesPayable from "@/pages/FeesPayable";
import VLATools from "@/pages/VLATools";
import LACWBilling from "@/pages/LACWBilling";
import TrainingGuide from "@/pages/TrainingGuide";
import BacksheetToCounsel from "@/pages/BacksheetToCounsel";
import TravelClaims from "@/pages/TravelClaims";
import ApplyForAid from "@/pages/ApplyForAid";
import StandAloneGuidelines from "@/pages/StandAloneGuidelines";
import RequestQueue from "@/pages/RequestQueue";
import CertificationReview from "@/pages/CertificationReview";
import VALSIntakeForm from "@/pages/VALSIntakeForm";
import VALSAuthorityToAct from "@/pages/VALSAuthorityToAct";
import ConflictCheck from "@/pages/ConflictCheck";
import Inbox from "@/pages/Inbox";
import OutlookMail from "@/pages/OutlookMail";
import OutlookAddin from "@/pages/OutlookAddin";
import ClsConnectDocs from "@/pages/ClsConnectDocs";
import ClsConnectMobile from "@/pages/ClsConnectMobile";
import PmsLab from "@/pages/PmsLab";
import ClientAccountDashboard from "@/pages/ClientAccountDashboard";
import { canAccessPortal, getRequestedPortal } from "@/lib/portalAccess";

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) =>
  Layout ? (
    <Layout currentPageName={currentPageName}>{children}</Layout>
  ) : (
    <>{children}</>
  );

const AuthenticatedApp = () => {
  const {
    user,
    isLoadingAuth,
    isLoadingPublicSettings,
    authError,
    navigateToLogin,
  } = useAuth();
  const location = useLocation();
  const requestedPortal = getRequestedPortal(location.pathname);
  const showBundleBar =
    !requestedPortal &&
    !["/", "/Home", "/StandAloneGuidelines", "/inbox", "/Inbox"].includes(
      location.pathname,
    );

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authError) {
    if (authError.type === "user_not_registered") {
      return <UserNotRegisteredError />;
    } else if (authError.type === "auth_required") {
      navigateToLogin();
      return null;
    }
  }

  if (!canAccessPortal(user, requestedPortal)) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 px-6">
        <section
          className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm"
          aria-labelledby="portal-access-title"
        >
          <div
            className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-red-50 text-xl"
            aria-hidden="true"
          >
            !
          </div>
          <h1
            id="portal-access-title"
            className="mt-5 text-xl font-bold text-slate-900"
          >
            Portal access unavailable
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Your account is not assigned to this client portal. Use your
            assigned portal link or contact Complete Law Support.
          </p>
        </section>
      </main>
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <LayoutWrapper currentPageName={mainPageKey}>
              <MainPage />
            </LayoutWrapper>
          }
        />
        {Object.entries(Pages).map(([path, Page]) => (
          <Route
            key={path}
            path={`/${path}`}
            element={
              <LayoutWrapper currentPageName={path}>
                <Page />
              </LayoutWrapper>
            }
          />
        ))}
        <Route
          path="/MeansCalculator"
          element={
            <LayoutWrapper currentPageName="MeansCalculator">
              <MeansCalculator />
            </LayoutWrapper>
          }
        />
        <Route
          path="/WhatAidDoINeed"
          element={
            <LayoutWrapper currentPageName="WhatAidDoINeed">
              <WhatAidDoINeed />
            </LayoutWrapper>
          }
        />
        <Route
          path="/FeesPayable"
          element={
            <LayoutWrapper currentPageName="FeesPayable">
              <FeesPayable />
            </LayoutWrapper>
          }
        />
        <Route
          path="/VLATools"
          element={
            <LayoutWrapper currentPageName="VLATools">
              <VLATools />
            </LayoutWrapper>
          }
        />
        <Route
          path="/LACWBilling"
          element={
            <LayoutWrapper currentPageName="LACWBilling">
              <LACWBilling />
            </LayoutWrapper>
          }
        />
        <Route
          path="/TrainingGuide"
          element={
            <LayoutWrapper currentPageName="TrainingGuide">
              <TrainingGuide />
            </LayoutWrapper>
          }
        />
        <Route
          path="/BacksheetToCounsel"
          element={
            <LayoutWrapper currentPageName="BacksheetToCounsel">
              <BacksheetToCounsel />
            </LayoutWrapper>
          }
        />
        <Route
          path="/TravelClaims"
          element={
            <LayoutWrapper currentPageName="TravelClaims">
              <TravelClaims />
            </LayoutWrapper>
          }
        />
        <Route
          path="/ApplyForAid"
          element={
            <LayoutWrapper currentPageName="ApplyForAid">
              <ApplyForAid />
            </LayoutWrapper>
          }
        />
        <Route
          path="/StandAloneGuidelines"
          element={
            <LayoutWrapper currentPageName="StandAloneGuidelines">
              <StandAloneGuidelines />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-portal"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-portal"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-portal"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" />
            </LayoutWrapper>
          }
        />
        {/* VALS tool routes */}
        <Route
          path="/vals-matter-workspace"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="matter-workspace"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-certification"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="certification"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-aidapplication"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="aid-form" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-applyforaid"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="apply-aid" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-intake"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="intake" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-authority"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="authority" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-backsheet"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="backsheet" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-medicalworksheet"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="medical-worksheet"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-memoprecedent"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="memo-precedent"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-emails"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="vals-emails" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-feeslipinvoicepayment"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="fee-slip-invoice-payment"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-submittedinvoices"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="submitted-invoices"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-monthlytrustreceipts"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="monthly-trust-receipts"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-appealcostcertificate"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="appeal-cost-certificate"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-draftforms"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="draft-forms" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-diary"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="vals-diary" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-additionalprepworksheet"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines
                brandType="vals"
                initialTab="additional-prep"
              />
            </LayoutWrapper>
          }
        />
        {/* LACW tool routes */}
        <Route
          path="/lacw-matter-workspace"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="matter-workspace"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-certification"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="certification"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-aidapplication"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="aid-form" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-applyforaid"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="apply-aid" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-intake"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="intake" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-backsheet"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="backsheet" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-medicalworksheet"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="medical-worksheet"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-memoprecedent"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="memo-precedent"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-travelclaim"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="travel-claim"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-billing"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="billing" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-appealcostcertificate"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="appeal-cost-certificate"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-emails"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="lacw-emails" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-feeslipinvoicepayment"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="fee-slip-invoice-payment"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-submittedinvoices"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="submitted-invoices"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-monthlytrustreceipts"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="monthly-trust-receipts"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-draftforms"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="draft-forms" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-additionalprepworksheet"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="additional-prep"
              />
            </LayoutWrapper>
          }
        />
        {/* Certification of legal aid applications - the link in the certifier's email lands here. */}
        <Route
          path="/lacw-certifications"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="certifications" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-certifications"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="certifications" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-certifications"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="certifications" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-certifications"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines brandType="jbl" initialTab="certifications" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-certifications"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines brandType="rga" initialTab="certifications" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-staff-calendar"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="staff-calendar"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-reminders-guide"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="reminders-guide" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-reminders-guide"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="reminders-guide" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-diary-codes"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="diary-codes" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-diary-codes"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="diary-codes" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-aid-planner"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="aid-planner" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/vals-aid-planner"
          element={
            <LayoutWrapper currentPageName="vals-portal">
              <StandAloneGuidelines brandType="vals" initialTab="aid-planner" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-aid-diary"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="aid-diary" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-outlook-mail"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines
                brandType="lacw"
                initialTab="outlook-mail"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-actionstep-simulator"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="actionstep-simulator" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-atlas-letter-downloader"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="atlas-letter-downloader" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-atlas-simulator"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              <StandAloneGuidelines brandType="lacw" initialTab="atlas-simulator" />
            </LayoutWrapper>
          }
        />
        {/* EAS tool routes */}
        <Route
          path="/eas-diary"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="eas-diary" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-emails"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="eas-emails" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-feeslipinvoicepayment"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="fee-slip-invoice-payment" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-submittedinvoices"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="submitted-invoices" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-monthlytrustreceipts"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="monthly-trust-receipts" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-appealcostcertificate"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="appeal-cost-certificate" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-matter-workspace"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines
                brandType="eas"
                initialTab="matter-workspace"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-certification"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines
                brandType="eas"
                initialTab="certification"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-aidapplication"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="aid-form" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-applyforaid"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="apply-aid" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-draftforms"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines brandType="eas" initialTab="draft-forms" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/eas-additionalprepworksheet"
          element={
            <LayoutWrapper currentPageName="eas-portal">
              <StandAloneGuidelines
                brandType="eas"
                initialTab="additional-prep"
              />
            </LayoutWrapper>
          }
        />
        {/* JBL tool routes */}
        <Route
          path="/jbl-portal"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines brandType="jbl" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-matter-workspace"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines
                brandType="jbl"
                initialTab="matter-workspace"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-certification"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines
                brandType="jbl"
                initialTab="certification"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-aidapplication"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines brandType="jbl" initialTab="aid-form" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-applyforaid"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines brandType="jbl" initialTab="apply-aid" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-intake"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines brandType="jbl" initialTab="intake" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-authority"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines brandType="jbl" initialTab="authority" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-backsheet"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines brandType="jbl" initialTab="backsheet" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-medicalworksheet"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines
                brandType="jbl"
                initialTab="medical-worksheet"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-memoprecedent"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines
                brandType="jbl"
                initialTab="memo-precedent"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-conflictcheck"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <ConflictCheck isPortalMode={true} brandType="jbl" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-training-guide"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines
                brandType="jbl"
                initialTab="training-guide"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-draftforms"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines brandType="jbl" initialTab="draft-forms" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/jbl-additionalprepworksheet"
          element={
            <LayoutWrapper currentPageName="jbl-portal">
              <StandAloneGuidelines
                brandType="jbl"
                initialTab="additional-prep"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/VALSIntakeForm"
          element={
            <LayoutWrapper currentPageName="VALSIntakeForm">
              <VALSIntakeForm />
            </LayoutWrapper>
          }
        />
        <Route
          path="/VALSAuthorityToAct"
          element={
            <LayoutWrapper currentPageName="VALSAuthorityToAct">
              <VALSAuthorityToAct />
            </LayoutWrapper>
          }
        />
        <Route
          path="/inbox"
          element={
            <LayoutWrapper currentPageName="Inbox">
              <Inbox />
            </LayoutWrapper>
          }
        />
        <Route
          path="/Inbox"
          element={
            <LayoutWrapper currentPageName="Inbox">
              <Inbox />
            </LayoutWrapper>
          }
        />
        <Route
          path="/outlook-mail"
          element={
            <LayoutWrapper currentPageName="OutlookMail">
              <OutlookMail />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-portal"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-matter-workspace"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="matter-workspace"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-inbox"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" initialTab="inbox" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-weekly-summary"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" initialTab="weekly-summary" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-draftforms"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="draft-forms"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-certifications"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              {/* Choose a firm, set its certifiers, and see its certifications. */}
              <CertificationReview />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-request-queue"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <RequestQueue />
            </LayoutWrapper>
          }
        />
        <Route
          path="/lacw-request-queue"
          element={
            <LayoutWrapper currentPageName="lacw-portal">
              {/* The firm's own view: read-only, its own requests only. The API enforces
                  that from the session, so this cannot be widened by changing the prop. */}
              <RequestQueue brand="lacw" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-audit-logs"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" initialTab="audit-logs" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-deadline-guard"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="deadline-guard"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-lacw-client-account"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <ClientAccountDashboard clientId="lacw" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-vals-client-account"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <ClientAccountDashboard clientId="vals" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-eas-client-account"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <ClientAccountDashboard clientId="eas" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-easfeeslipinvoicepayment"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" initialTab="eas-fee-slip-invoice-payment" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-eastrustreceipts"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" initialTab="eas-trust-receipts" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-easemails"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" initialTab="eas-emails" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-billing"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" initialTab="billing" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-appealcostcertificate"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="appeal-cost-certificate"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-lacw-appealcostcertificate"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="lacw-appeal-cost-certificate"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-vals-diary"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="vals-diary"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-eas-diary"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="eas-diary"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-vals-appealcostcertificate"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="vals-appeal-cost-certificate"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-eas-appealcostcertificate"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="eas-appeal-cost-certificate"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-lacw-feeslipinvoicepayment"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="lacw-fee-slip-invoice-payment"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-lacwtrustreceipts"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="lacw-trust-receipts"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-lacwemails"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="lacw-emails"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-lacw-aid-planner"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="lacw-aid-planner"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-lacw-staff-calendar"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="lacw-staff-calendar"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-rgafeeslipinvoicepayment"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="rga-fee-slip-invoice-payment"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-rgatrustreceipts"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="rga-trust-receipts"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-rgaemails"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines brandType="admin" initialTab="rga-emails" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-valsfeeslipinvoicepayment"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="vals-fee-slip-invoice-payment"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-valsdisbursements"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="vals-trust-receipts"
              />
            </LayoutWrapper>
          }
        />
        {/* The page's old address, from when it held trust receipts: kept so bookmarks and
            open tabs still arrive. */}
        <Route path="/admin-valstrustreceipts" element={<Navigate to="/admin-valsdisbursements" replace />} />
        <Route
          path="/admin-valsemails"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="vals-emails"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/admin-additionalprepworksheet"
          element={
            <LayoutWrapper currentPageName="admin-portal">
              <StandAloneGuidelines
                brandType="admin"
                initialTab="additional-prep"
              />
            </LayoutWrapper>
          }
        />

        {/* RGA tool routes */}
        <Route
          path="/rga-portal"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines brandType="rga" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-matter-workspace"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines
                brandType="rga"
                initialTab="matter-workspace"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-certification"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines
                brandType="rga"
                initialTab="certification"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-aidapplication"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines brandType="rga" initialTab="aid-form" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-applyforaid"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines brandType="rga" initialTab="apply-aid" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-appealcostcertificate"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines
                brandType="rga"
                initialTab="appeal-cost-certificate"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-medicalworksheet"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines
                brandType="rga"
                initialTab="medical-worksheet"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-memoprecedent"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines
                brandType="rga"
                initialTab="memo-precedent"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-emails"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines brandType="rga" initialTab="rga-emails" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-feeslipinvoicepayment"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines
                brandType="rga"
                initialTab="fee-slip-invoice-payment"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-monthlytrustreceipts"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines
                brandType="rga"
                initialTab="monthly-trust-receipts"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-draftforms"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines brandType="rga" initialTab="draft-forms" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/rga-additionalprepworksheet"
          element={
            <LayoutWrapper currentPageName="rga-portal">
              <StandAloneGuidelines
                brandType="rga"
                initialTab="additional-prep"
              />
            </LayoutWrapper>
          }
        />

        {/* Counsel tool routes */}
        <Route
          path="/counsel-portal"
          element={
            <LayoutWrapper currentPageName="counsel-portal">
              <StandAloneGuidelines brandType="counsel" />
            </LayoutWrapper>
          }
        />
        <Route
          path="/counsel-matter-workspace"
          element={
            <LayoutWrapper currentPageName="counsel-portal">
              <StandAloneGuidelines
                brandType="counsel"
                initialTab="matter-workspace"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/counsel-backsheet"
          element={
            <LayoutWrapper currentPageName="counsel-portal">
              <StandAloneGuidelines
                brandType="counsel"
                initialTab="backsheet"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/counsel-travelclaim"
          element={
            <LayoutWrapper currentPageName="counsel-portal">
              <StandAloneGuidelines
                brandType="counsel"
                initialTab="travel-claim"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/counsel-draftforms"
          element={
            <LayoutWrapper currentPageName="counsel-portal">
              <StandAloneGuidelines
                brandType="counsel"
                initialTab="draft-forms"
              />
            </LayoutWrapper>
          }
        />
        <Route
          path="/counsel-additionalprepworksheet"
          element={
            <LayoutWrapper currentPageName="counsel-portal">
              <StandAloneGuidelines
                brandType="counsel"
                initialTab="additional-prep"
              />
            </LayoutWrapper>
          }
        />

        <Route path="/outlook-taskpane" element={<OutlookAddin />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </>
  );
};

function App() {
  const isOfficeTaskpane = window.location.pathname === "/outlook-taskpane";
  const isClsConnectDocs = window.location.pathname === "/cls-connect-docs";
  const isPmsLab = window.location.pathname === "/pms-lab";
  const isCapacitorShell =
    window.location.protocol === "capacitor:" ||
    window.Capacitor?.isNativePlatform?.() === true;
  const isClsConnectMobile =
    window.location.pathname === "/cls-connect-mobile" || isCapacitorShell;

  if (isClsConnectDocs) {
    return <ClsConnectDocs />;
  }

  if (isPmsLab) {
    return <PmsLab />;
  }

  if (isClsConnectMobile) {
    return (
      <QueryClientProvider client={queryClientInstance}>
        <ClsConnectMobile />
        <Toaster />
        <DegradedStorageNotice />
        <SonnerToaster richColors position="top-center" />
      </QueryClientProvider>
    );
  }

  if (isOfficeTaskpane) {
    return (
      <QueryClientProvider client={queryClientInstance}>
        <MemoryRouter
          initialEntries={[
            `${window.location.pathname}${window.location.search}`,
          ]}
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <Routes>
            <Route path="/outlook-taskpane" element={<OutlookAddin />} />
          </Routes>
        </MemoryRouter>
        <Toaster />
        <DegradedStorageNotice />
        <SonnerToaster richColors position="top-right" />
      </QueryClientProvider>
    );
  }

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <NavigationTracker />
          <AuthenticatedApp />
        </BrowserRouter>
        <Toaster />
        <DegradedStorageNotice />
        <SonnerToaster richColors position="top-right" />
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
